import {
  DocumentNode,
  makeReference,
  Reference,
  StoreObject,
  useApolloClient,
} from '@apollo/client'
import { ReadFieldFunction } from '@apollo/client/cache/core/types/common'
import { Query } from '../generated/graphql'

type CacheItem = { id: string } & StoreObject

type CacheHelpers = {
  getFragment<T>(id: string, itemFragment: DocumentNode, fallback?: T): T

  setField<
    TItem extends CacheItem,
    TTarget extends CacheItem,
    K extends keyof TTarget,
  >(
    itemFragment: DocumentNode,
    item: TItem | null,
    target: TTarget,
    fieldName: K,
  ): void

  addItemToList<
    TItem extends CacheItem,
    TTarget extends CacheItem,
    K extends keyof TTarget,
  >(
    itemFragment: DocumentNode,
    item: TItem,
    target: TTarget,
    fieldName: K,
  ): void

  addItemToListInRootQuery<TItem extends CacheItem, K extends keyof Query>(
    itemFragment: DocumentNode,
    item: TItem,
    fieldName: K,
  ): void

  removeItemFromList<TTarget extends CacheItem, K extends keyof TTarget>(
    itemFragment: DocumentNode,
    itemId: string,
    target: TTarget,
    fieldName: K,
  ): void

  removeItemFromListInRootQuery<K extends keyof Query>(
    itemFragment: DocumentNode,
    itemId: string,
    fieldName: K,
  ): void
}

const getFragmentName = (fragment: DocumentNode) => {
  const firstDefinition = fragment.definitions?.[0]
  if (firstDefinition?.kind !== 'FragmentDefinition') {
    throw new Error('Given fragment does not seem to be a fragment')
  }

  return firstDefinition.name.value
}

const getFragmentTypeName = (fragment: DocumentNode) => {
  const firstDefinition = fragment.definitions?.[0]
  if (firstDefinition?.kind !== 'FragmentDefinition') {
    throw new Error('Given fragment does not seem to be a fragment')
  }

  return firstDefinition.typeCondition.name.value
}

const getFragmentId = (itemId: string, fragment: DocumentNode) =>
  `${getFragmentTypeName(fragment)}:${itemId}`

export function useCacheHelpers(): CacheHelpers {
  const apolloClient = useApolloClient()
  const { cache } = apolloClient
  const rootQueryReference = makeReference('ROOT_QUERY')

  function addItemToListCommon<
    TItem extends CacheItem,
    TTarget extends CacheItem | Reference,
    K extends keyof TTarget | keyof Query,
  >(
    itemFragment: DocumentNode,
    item: TItem,
    fieldName: K,
    target?: TTarget,
  ): void {
    cache.modify({
      id: cache.identify(target ?? rootQueryReference),

      fields: {
        [fieldName]: (
          existingRefs: Reference[],
          { readField }: { readField: ReadFieldFunction },
        ) => {
          const fragmentName = getFragmentName(itemFragment)
          const newRef = cache.writeFragment({
            data: item,
            fragment: itemFragment,
            fragmentName,
          })

          if (existingRefs.some((ref) => readField('id', ref) === item.id)) {
            return existingRefs
          }

          return [...existingRefs, newRef]
        },
      },
    })
  }

  function removeItemFromListCommon<
    TTarget extends CacheItem,
    K extends keyof TTarget | keyof Query,
  >(
    itemFragment: DocumentNode,
    itemId: string,
    fieldName: K,
    target?: TTarget,
  ): void {
    cache.modify({
      id: cache.identify(target ?? rootQueryReference),

      fields: {
        [fieldName]: (
          existingRefs: Reference[],
          { readField }: { readField: ReadFieldFunction },
        ) => {
          const fragmentId = getFragmentId(itemId, itemFragment)
          cache.removeOptimistic(fragmentId)

          const newRefs = existingRefs.filter(
            (ref) => readField('id', ref) !== itemId,
          )

          return [...newRefs]
        },
      },
    })
  }

  return {
    getFragment<T>(id: string, fragment: DocumentNode, fallback?: T): T {
      const fragmentName = getFragmentName(fragment)

      const result = cache.readFragment<T>({
        id: getFragmentId(id, fragment),
        fragment,
        fragmentName,
      })

      if (!result) {
        if (fallback === undefined) {
          throw new Error(`Fragment "${id}" not found in cache`)
        }

        return fallback
      }

      return result
    },

    setField<
      TItem extends CacheItem,
      TTarget extends CacheItem,
      K extends keyof TTarget,
    >(
      itemFragment: DocumentNode,
      item: TItem | null,
      target: TTarget,
      fieldName: K,
    ): void {
      cache.modify({
        id: cache.identify(target),

        fields: {
          [fieldName]: () => {
            if (!item) {
              return null
            }

            const fragmentName = getFragmentName(itemFragment)

            return cache.writeFragment({
              data: item,
              fragment: itemFragment,
              fragmentName,
            })
          },
        },
      })
    },

    addItemToList<
      TItem extends CacheItem,
      TTarget extends CacheItem,
      K extends keyof TTarget,
    >(
      itemFragment: DocumentNode,
      item: TItem,
      target: TTarget,
      fieldName: K,
    ): void {
      addItemToListCommon(itemFragment, item, fieldName, target)
    },

    addItemToListInRootQuery<Item extends CacheItem, K extends keyof Query>(
      itemFragment: DocumentNode,
      item: Item,
      fieldName: K,
    ): void {
      addItemToListCommon(itemFragment, item, fieldName)
    },

    removeItemFromList<TTarget extends CacheItem, K extends keyof TTarget>(
      itemFragment: DocumentNode,
      itemId: string,
      target: TTarget,
      fieldName: K,
    ): void {
      removeItemFromListCommon(itemFragment, itemId, fieldName, target)
    },

    removeItemFromListInRootQuery<K extends keyof Query>(
      itemFragment: DocumentNode,
      itemId: string,
      fieldName: K,
    ): void {
      removeItemFromListCommon(itemFragment, itemId, fieldName)
    },
  }
}
